import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const ContactSection = styled.section`
  padding: 8rem 5%;
  background-color: var(--background);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  h2 {
    font-size: clamp(2.5rem, 4vw, 4rem);
    margin-bottom: 1rem;
    color: var(--text-primary);
    line-height: 1.1;
  }

  p {
    font-size: 2.5rem;
    color: var(--primary);
    font-weight: 700;
    margin-bottom: 3rem;
  }

  .contact-info {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    a {
      display: flex;
      align-items: center;
      color: var(--text-primary);
      font-size: 1.25rem;
      transition: color 0.3s;

      &:hover {
        color: var(--secondary);
      }
    }
  }
`;

const FormContainer = styled.div`
  background: #ffffff;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    color: #333333;
    font-size: 0.9rem;
    font-weight: 600;
  }
`;

const Input = styled.input`
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e1e1e1;
  background: #f9f9f9;
  color: #333;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #0066cc;
  }
`;

const Select = styled.select`
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e1e1e1;
  background: #f9f9f9;
  color: #333;
  font-size: 1rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #0066cc;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  color: #666666;
  font-size: 0.9rem;

  input {
    width: 1.25rem;
    height: 1.25rem;
    cursor: pointer;
  }
`;

const SubmitButton = styled.button`
  padding: 1rem 2rem;
  border-radius: 0.25rem;
  border: none;
  background: #0066cc;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
  margin-top: 1rem;
  align-self: flex-start;

  &:hover {
    background: #005bb5;
  }
`;

export const Contact: React.FC = () => {
  const { t } = useTranslation();
  return (
    <ContactSection>
      <Container>
        <LeftContent>
          <h2>
            {t("contact.title_part1")} <br /> {t("contact.title_part2")} <br />{" "}
            {t("contact.title_part3")}
          </h2>
          <p>{t("contact.subtitle")}</p>
          <div className="contact-info">
            <a href="mailto:sales@betpilot.tech">
              <span style={{ color: "var(--primary)", marginRight: "1rem" }}>
                ✉
              </span>
              sales@betpilot.tech
            </a>
            <a href="tel:+5581992077621">
              <span style={{ color: "var(--primary)", marginRight: "1rem" }}>
                ☏
              </span>
              +55 81 99207-7621
            </a>
          </div>
        </LeftContent>
        <FormContainer>
          <Form onSubmit={(e) => e.preventDefault()}>
            <FormRow>
              <FormGroup>
                <label>{t("contact.first_name")}</label>
                <Input type="text" required />
              </FormGroup>
              <FormGroup>
                <label>{t("contact.last_name")}</label>
                <Input type="text" required />
              </FormGroup>
            </FormRow>
            <FormGroup>
              <label>{t("contact.phone")}</label>
              <Input type="tel" required />
            </FormGroup>
            <FormGroup>
              <label>{t("contact.email")}</label>
              <Input type="email" required />
            </FormGroup>
            <FormGroup>
              <label>{t("contact.where")}</label>
              <Select required>
                <option value="Facebook">{t("contact.where_opt1")}</option>
                <option value="Google">{t("contact.where_opt2")}</option>
                <option value="LinkedIn">{t("contact.where_opt3")}</option>
                <option value="Other">{t("contact.where_opt4")}</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <label>{t("contact.consent")}</label>
              <CheckboxLabel>
                <input type="checkbox" required />
                {t("contact.consent_check")}
              </CheckboxLabel>
            </FormGroup>
            <SubmitButton type="submit">{t("contact.submit")}</SubmitButton>
          </Form>
        </FormContainer>
      </Container>
    </ContactSection>
  );
};

